import React from 'react';
import { motion } from 'framer-motion';
import { useSEO } from '../hooks/useSEO';

const sections = [
  {
    heading: 'Introduction',
    body: [
      'Hei Web Creations Pte Ltd ("we", "us", or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or engage our services.',
      'By using our website and services, you consent to the practices described in this policy. This policy is designed to comply with the Personal Data Protection Act 2012 (PDPA) of Singapore.',
    ],
  },
  {
    heading: 'Information We Collect',
    body: [
      'We collect information you voluntarily provide when you submit an enquiry through our contact form, including your name, email address, service type, and project description. We may also collect technical information such as your browser type and pages visited to improve our website.',
    ],
  },
  {
    heading: 'How We Use Your Information',
    body: [
      'We use the information we collect to respond to your enquiries, provide and improve our services, communicate with you about your project, and comply with legal obligations. We do not sell your personal data to third parties.',
    ],
  },
  {
    heading: 'Data Retention & Security',
    body: [
      'We retain personal data only for as long as necessary to fulfil the purposes for which it was collected, or as required by law. We implement reasonable technical and organisational measures to protect your data against unauthorised access, loss, or misuse.',
    ],
  },
  {
    heading: 'Your Rights',
    body: [
      'Under the PDPA, you have the right to request access to, and correction of, the personal data we hold about you. You may also withdraw consent for us to use your data at any time. To exercise these rights, please contact us using the details below.',
    ],
  },
  {
    heading: 'Cookies',
    body: [
      'Our website may use cookies and similar technologies to enhance your browsing experience and analyse site usage. You can control cookies through your browser settings.',
    ],
  },
  {
    heading: 'Contact Us',
    body: [
      'If you have any questions about this Privacy Policy or how we handle your data, please contact us at hello@heiwebcreations.com or +65 9888 3331.',
    ],
  },
];

const PrivacyPolicy = () => {
  useSEO({
    title: 'Privacy Policy | Hei Web Creations Singapore',
    description:
      'Read the Privacy Policy of Hei Web Creations Pte Ltd — how we collect, use, and protect your personal data in compliance with Singapore PDPA.',
  });

  return (
    <div className="pt-32 bg-white" data-testid="privacy-policy-page">
      <section className="py-16 border-b border-gray-100">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="font-mono text-sm text-[#C8102E]">~/legal</span>
              <h1 className="text-5xl md:text-6xl font-bold text-[#1A1A1A] mt-4 mb-4 tracking-tight">
                Privacy Policy
              </h1>
              <p className="font-mono text-sm text-gray-400">Last updated: January 2026</p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.03 }}
                viewport={{ once: true }}
                className="mb-12"
              >
                <h2 className="text-2xl md:text-3xl font-bold text-[#1A1A1A] mb-4 tracking-tight">
                  {section.heading}
                </h2>
                {section.body.map((para, i) => (
                  <p key={i} className="text-lg text-gray-600 leading-[1.8] mb-4">
                    {para}
                  </p>
                ))}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
